// /academy/calculadora — Risk & position-sizing calculator.
// Two-column layout (mobile stacks): inputs on the left, live results on
// the right. All math runs client-side; no backend involvement.
import React, { useMemo, useState } from "react";
import { Calculator, TrendingDown, TrendingUp } from "lucide-react";
import AcademyShell from "../components/academy/AcademyShell";
import { useLanguage } from "../i18n/LanguageContext";
import { getAcademyToolsI18n } from "../i18n/academy-tools";
import { calcular, CALIFICACION_COLOR } from "../lib/calculadora-riesgo";

const LEV_OPTIONS = [1, 2, 5, 10, 20, 50, 100];

const FieldLabel = ({ children, hint }) => (
  <div className="mb-1.5 flex items-baseline justify-between">
    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/55">
      {children}
    </label>
    {hint && <span className="text-[10px] text-white/35">{hint}</span>}
  </div>
);

const NumInput = ({ value, onChange, step = 1, min = 0, testid }) => (
  <input
    type="number"
    value={value}
    step={step}
    min={min}
    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-sm text-white outline-none transition focus:border-orange-400/50 focus:bg-black/60"
    data-testid={testid}
  />
);

const AcademyCalculadoraPage = () => {
  const { lang } = useLanguage();
  const T = getAcademyToolsI18n(lang);
  const [capital, setCapital] = useState(10000);
  const [riesgoPorc, setRiesgoPorc] = useState(1);
  const [direccion, setDireccion] = useState("long");
  const [entrada, setEntrada] = useState(45000);
  const [stopLoss, setStopLoss] = useState(43500);
  const [objetivo, setObjetivo] = useState(48000);
  const [apalancamiento, setApalancamiento] = useState(1);

  const r = useMemo(
    () =>
      calcular({
        capital,
        riesgoPorc,
        entrada,
        stopLoss,
        objetivo,
        apalancamiento,
        direccion,
      }),
    [capital, riesgoPorc, entrada, stopLoss, objetivo, apalancamiento, direccion]
  );

  const stars = Math.max(1, Math.min(5, Math.round(r.ratioRR)));

  return (
    <AcademyShell
      testid="academy-calculadora"
      eyebrow={T.calc_eyebrow}
      eyebrowIcon={Calculator}
      title={T.calc_title}
      subtitle={T.calc_subtitle}
      breadcrumbCurrent={T.calc_breadcrumb}
      footHint={T.calc_foot}
    >
      <div className="grid gap-6 lg:grid-cols-12">
        {/* INPUTS */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl lg:col-span-7">
          <h3 className="mb-5 text-xs font-black uppercase tracking-[0.25em] text-white/70">
            {T.calc_inputs}
          </h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel>{T.calc_capital}</FieldLabel>
              <NumInput value={capital} onChange={setCapital} step={100} testid="calc-capital" />
            </div>
            <div>
              <FieldLabel hint={`${riesgoPorc.toFixed(1)} %`}>{T.calc_risk}</FieldLabel>
              <input
                type="range"
                min="0.5"
                max="5"
                step="0.5"
                value={riesgoPorc}
                onChange={(e) => setRiesgoPorc(parseFloat(e.target.value))}
                className="w-full accent-orange-500"
                data-testid="calc-riesgo"
              />
            </div>

            <div className="sm:col-span-2">
              <FieldLabel>{T.calc_direction}</FieldLabel>
              <div className="flex gap-2">
                {[
                  { id: "long", label: T.calc_long, Icon: TrendingUp, color: "emerald" },
                  { id: "short", label: T.calc_short, Icon: TrendingDown, color: "red" },
                ].map((d) => {
                  const active = direccion === d.id;
                  return (
                    <button
                      key={d.id}
                      onClick={() => setDireccion(d.id)}
                      data-testid={`calc-dir-${d.id}`}
                      className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] transition-all ${
                        active
                          ? d.id === "long"
                            ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-200"
                            : "border-red-400/60 bg-red-500/15 text-red-200"
                          : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
                      }`}
                    >
                      <d.Icon className="h-3.5 w-3.5" />
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <FieldLabel>{T.calc_entry}</FieldLabel>
              <NumInput value={entrada} onChange={setEntrada} step={1} testid="calc-entrada" />
            </div>
            <div>
              <FieldLabel hint={`${r.porcentajeStop.toFixed(2)} %`}>{T.calc_sl}</FieldLabel>
              <NumInput value={stopLoss} onChange={setStopLoss} step={1} testid="calc-sl" />
            </div>
            <div>
              <FieldLabel hint={`${r.porcentajeObjetivo.toFixed(2)} %`}>{T.calc_tp}</FieldLabel>
              <NumInput value={objetivo} onChange={setObjetivo} step={1} testid="calc-tp" />
            </div>
            <div>
              <FieldLabel>{T.calc_lev}</FieldLabel>
              <div className="flex flex-wrap gap-1.5">
                {LEV_OPTIONS.map((lev) => {
                  const active = apalancamiento === lev;
                  return (
                    <button
                      key={lev}
                      onClick={() => setApalancamiento(lev)}
                      data-testid={`calc-lev-${lev}`}
                      className={`min-w-[44px] rounded-md border px-2 py-1 text-[11px] font-bold ${
                        active
                          ? "border-orange-400/60 bg-orange-500/15 text-orange-200"
                          : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
                      }`}
                    >
                      {lev}x
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="lg:col-span-5">
          <div
            className={`rounded-2xl border p-5 backdrop-blur-xl ${
              CALIFICACION_COLOR[r.calificacion]
            }`}
            data-testid="calc-result"
          >
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.25em] opacity-70">
              {T.calc_analysis}
            </div>
            <div className="mb-4 flex items-baseline justify-between">
              <span className="text-2xl font-black uppercase tracking-tight">
                {T.calc_grade[r.calificacion] || r.calificacion}
              </span>
              <span className="font-mono text-xs opacity-70">
                {"★".repeat(stars)}
                {"☆".repeat(5 - stars)}
              </span>
            </div>

            <div className="space-y-1.5 border-y border-current/20 py-3 font-mono text-[12px] opacity-95">
              <Row k={T.calc_risk_max} v={`€ ${r.riesgoEuros.toLocaleString()}`} hint={`${riesgoPorc}%`} />
              <Row k={T.calc_potential} v={`€ ${r.gananciaEuros.toLocaleString()}`} hint={`+${r.porcentajeObjetivo.toFixed(1)}%`} />
              <Row k={T.calc_rr} v={`1 : ${r.ratioRR.toFixed(2)}`} highlight />
            </div>

            <div className="space-y-1.5 pt-3 font-mono text-[12px] opacity-80">
              <Row k={T.calc_size} v={`€ ${r.tamanoPosicion.toLocaleString()}`} />
              <Row k={T.calc_qty} v={`${r.cantidadMonedas} ud.`} />
              <Row k={T.calc_dist_sl} v={`-${r.porcentajeStop.toFixed(2)} %`} />
              <Row k={T.calc_dist_tp} v={`+${r.porcentajeObjetivo.toFixed(2)} %`} />
              <Row k={T.calc_liq} v={`€ ${r.precioLiquidacion.toLocaleString()}`} />
            </div>

            {r.mensajes.length > 0 && (
              <ul className="mt-4 space-y-1.5 text-[12px]">
                {r.mensajes.map((m, i) => (
                  <li key={i} className="leading-snug">{m}</li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* EDUCATIONAL SECTION */}
      <section className="mt-10 grid gap-5 lg:grid-cols-3">
        <Callout title={T.calc_how_title}>
          <ol className="list-inside list-decimal space-y-1 text-[12px] text-white/65">
            {T.calc_how.map((line, i) => (<li key={i}>{line}</li>))}
          </ol>
        </Callout>

        <Callout title={T.calc_rules_title}>
          <ul className="space-y-1 text-[12px] text-white/65">
            {T.calc_rules.map((line, i) => (<li key={i}>• {line}</li>))}
          </ul>
        </Callout>

        <Callout title={T.calc_rr_title}>
          <p className="text-[12px] leading-relaxed text-white/65">{T.calc_rr_text}</p>
        </Callout>
      </section>
    </AcademyShell>
  );
};

const Row = ({ k, v, hint, highlight }) => (
  <div className="flex items-center justify-between">
    <span className={highlight ? "font-bold" : ""}>{k}</span>
    <span className={highlight ? "font-black text-base" : ""}>
      {v}
      {hint && <span className="ml-1 opacity-60">({hint})</span>}
    </span>
  </div>
);

const Callout = ({ title, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl">
    <h4 className="mb-3 text-[11px] font-black uppercase tracking-[0.22em] text-orange-300">
      {title}
    </h4>
    {children}
  </div>
);

export default AcademyCalculadoraPage;
